import React, { useState, useEffect, useMemo, use } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../../layouts";
import { toast } from "react-toastify";
import DynamicTable from "../../components/DynamicTable";
import apiService from "../../services/apiService";

export default function EventTicketInfo() {
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  // const [currentPage, setCurrentPage] = useState(1);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // ✅ Get eventId from query params
  const navigate = useNavigate();
  const location = useLocation();
  const queryEventId = new URLSearchParams(location.search).get("eventId");

  const [eventId, setEventId] = useState<any>(queryEventId);

  const columns = [
    { key: "ticket_name", label: "Ticket Name" },
    {
      key: "ticket_price",
      label: "Price ($)",
      render: (val: string | number) =>
        val && val !== "0" ? `$${val}` : "Free",
      isDecimal: true,
    },
    { key: "ticket_limit", label: "Limit" },
    { key: "available_quantity", label: "Available" },
    { key: "sold_quantity", label: "Sold" },
    { key: "refunded_quantity", label: "Refunded" },
    { key: "guests_count", label: "Guests" },
    {
      key: "promo_codes",
      label: "Promo Codes",
      render: (val: string | null) => {
        if (!val) return "—";

        const colors = [
          "bg-blue-100 text-blue-700",
          "bg-green-100 text-green-700",
          "bg-purple-100 text-purple-700",
          "bg-pink-100 text-pink-700",
          "bg-yellow-100 text-yellow-700",
          "bg-red-100 text-red-700",
        ];

        const codes = val.split(",").map((c) => c.trim());

        return (
          <div className="flex flex-wrap gap-1">
            {codes.map((code, idx) => (
              <span
                key={idx}
                className={`px-2 py-1 text-xs rounded ${
                  colors[idx % colors.length]
                }`}
              >
                {code}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: "is_passworded",
      label: "Password Protected",
      render: (val: boolean) => (val === true ? "Yes" : "No"),
    },
    {
      key: "password",
      label: "Password",
      render: (_: any, val: any) =>
        val?.is_passworded === true? (
          <button
            onClick={() => {
              navigator.clipboard.writeText(val?.password);
              toast.success("Password copied!");
            }}
            className="px-2 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600 transition"
          >
            Copy
          </button>
        ) : (
          "—"
        ),
    },
    { key: "event_id", label: "Event Id" },
    { key: "event_name", label: "Event Name" },
    {
      key: "sale_start_date",
      label: "Sale Start(Local)",
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      key: "sale_end_date",
      label: "Sale End(Local)",
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      key: "ticket_type_created_at",
      label: "Created At(Local)",
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      key: "ticket_type_updated_at",
      label: "Updated At(Local)",
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
  ];

  useEffect(() => {
    fetchTickets(eventId, page, search);
  }, [eventId, page, search]);

  const fetchTickets = async (
    eventId?: number,
    activePageNo?: number,
    searchTerm?: any
  ) => {
    try {
      setLoading(true);
      const params: any = {
        page: activePageNo,
        limit: 50,
        search: searchTerm,
        ...(eventId ? { eventId } : {}), // ✅ adds eventId only if it exists
      };

      const response: any = await apiService.get(
        "/event/ticket/get-event-ticket-details",
        params
      );
      setTickets(response?.data?.data || []); // ✅ updated: response.data is now event array
      setTotalRecords(response?.data?.totalCount || 0);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchKey?: string | undefined) => {
    console.log("Searching for Event ID:", searchKey);
    const params = new URLSearchParams(location.search);

    if (searchKey && searchKey.trim() !== "") {
      // ✅ Add/replace eventId
      params.set("eventId", searchKey.trim());
    } else {
      // ❌ searchKey empty → remove eventId
      params.delete("eventId");
    }

    // 🚀 Update URL (no reload)
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    // ✅ Update local state if needed
    setEventId(searchKey || "");
    setPage(1);
  };

  return (
    <Layout
      pageTitle="Tickets"
      eventId={eventId}
      searchByEventId={{
        label: "Search by Event ID",
        onClick: (val?: any) => {
          handleSearch(val);
        }, // 👈 your custom handler
        className: "bg-green-600 hover:bg-green-700",
      }}
    >
      <div className="min-h-screen bg-gray-50 py-2 px-4">
        {/* {tickets?.length > 0 && ( */}
        <DynamicTable
          title={`Tickets`}
          data={tickets}
          columns={columns}
          pageSize={pageSize}
          loading={loading}
          activeNUmber={page}
          onPageChange={(newPage) => {setPage(newPage);}}
          totalPages={totalPages}
          totalRecords={totalRecords}
          // onSearch={(val?:any) => {
          //   setPage(1)
          //   setSearch(val)
          // }}
        />
        {/* )} */}
      </div>
    </Layout>
  );
}
